import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationPanel = ({ notifications }) => {
  const [filter, setFilter] = useState('all');
  const [notificationList, setNotificationList] = useState(notifications || []);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'report':
        return { icon: 'FileText', color: 'text-primary', bgColor: 'bg-primary/10' };
      case 'alert':
        return { icon: 'AlertCircle', color: 'text-warning', bgColor: 'bg-warning/10' };
      case 'message':
        return { icon: 'Mail', color: 'text-accent', bgColor: 'bg-accent/10' };
      case 'system':
        return { icon: 'Settings', color: 'text-muted-foreground', bgColor: 'bg-muted/10' };
      default:
        return { icon: 'Bell', color: 'text-muted-foreground', bgColor: 'bg-muted/10' };
    }
  };

  const markAsRead = (notificationId) => {
    setNotificationList(prev =>
      prev?.map(notif =>
        notif?.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotificationList(prev => prev?.map(notif => ({ ...notif, read: true })));
  };

  const deleteNotification = (notificationId) => {
    setNotificationList(prev => prev?.filter(notif => notif?.id !== notificationId));
  };

  const filteredNotifications = filter === 'all'
    ? notificationList
    : filter === 'unread'
    ? notificationList?.filter(n => !n?.read)
    : notificationList?.filter(n => n?.type === filter);

  const unreadCount = notificationList?.filter(n => !n?.read)?.length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-lg border border-border agricultural-shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
            </p>
          </div>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="CheckCheck"
              iconPosition="left"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark All Read
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-2 mt-4 overflow-x-auto">
          {['all', 'unread', 'report', 'alert', 'message']?.map(filterType => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                filter === filterType
                  ? 'bg-primary text-white' :'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              {filterType?.charAt(0)?.toUpperCase() + filterType?.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Notification List */}
      <div className="bg-card rounded-lg border border-border agricultural-shadow">
        <div className="divide-y divide-border">
          {filteredNotifications?.length > 0 ? (
            filteredNotifications?.map(notification => {
              const iconConfig = getNotificationIcon(notification?.type);
              const timeAgo = getTimeAgo(notification?.timestamp);

              return (
                <div
                  key={notification?.id}
                  className={`p-4 hover:bg-muted/30 transition-colors ${
                    !notification?.read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-2 rounded-lg ${iconConfig?.bgColor} flex-shrink-0`}>
                      <Icon name={iconConfig?.icon} size={20} className={iconConfig?.color} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <p className={`text-sm ${
                          !notification?.read ? 'font-semibold text-foreground' : 'text-muted-foreground'
                        }`}>
                          {notification?.message}
                        </p>
                        {!notification?.read && (
                          <span className="ml-2 w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
                    </div>

                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {!notification?.read && (
                        <button
                          onClick={() => markAsRead(notification?.id)}
                          className="text-muted-foreground hover:text-primary transition-colors"
                          title="Mark as read"
                        >
                          <Icon name="Check" size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification?.id)}
                        className="text-muted-foreground hover:text-error transition-colors"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-12 text-center">
              <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No notifications found</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Integration Info */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Mail" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Email Notifications Enabled</p>
            <p className="text-xs text-muted-foreground mt-1">
              You'll receive email alerts for new reports and critical updates. Manage preferences in settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate time ago
const getTimeAgo = (timestamp) => {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  return past?.toLocaleDateString();
};

export default NotificationPanel;